import { FileText, Tag } from "lucide-react"
import type { FormEvent } from "react"

import { RichTextEditor } from "@/components/rich-text-editor"
import { Button } from "@/components/ui/button"
import { CardContent, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { TagsInput } from "@/components/ui/tags-input"

export interface ForumCategory {
  id: number
  name: string
}
export interface Neighborhood {
  id: number
  name: string
}

export type Visibility = "neighborhood" | "city" | "country" | "global"

export interface ForumPostFormData {
  title: string
  content: string
  category_id: string
  neighborhood_id: string
  visibility: Visibility
  is_pinned?: boolean
  tags?: string
}

export interface ForumPostFormProps {
  title: string
  data: ForumPostFormData
  errors: Record<string, string>
  categories: ForumCategory[]
  neighborhoods: Neighborhood[]
  processing?: boolean
  onChange: (partial: Partial<ForumPostFormData>) => void
  onSubmit: (e: FormEvent) => void
  onCancel: () => void
  submitLabel?: string
  can_pin?: boolean
}

export function ForumPostForm({
  title,
  data,
  errors,
  categories,
  neighborhoods,
  processing = false,
  onChange,
  onSubmit,
  onCancel,
  submitLabel = "Publicar",
  can_pin = false,
}: ForumPostFormProps) {
  const err = errors || {}

  return (
    <div className="w-full max-w-4xl">
      <CardTitle className="text-foreground flex items-center gap-3 px-4 text-2xl font-bold">
        <FileText className="text-primary h-8 w-8" />
        {title}
      </CardTitle>
      <CardContent className="px-4">
        {Object.keys(err).length > 0 && (
          <div className="alert-error mb-6">
            <div className="mb-2 flex items-center gap-2">
              <div className="bg-status-error h-2 w-2 rounded-full"></div>
              <p className="text-destructive font-semibold">
                Por favor corrige los siguientes errores:
              </p>
            </div>
            <ul className="text-destructive list-disc space-y-1 pl-6 text-sm">
              {Object.entries(err).map(([k, v]) => (
                <li key={k}>{v}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-4">
            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Título del Post
              </Label>
              <Input
                value={data.title}
                onChange={(e) => onChange({ title: e.target.value })}
                required
                className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                placeholder="Ej: Consejos para mudarse a Madrid"
              />
              {err.title && (
                <div className="text-destructive mt-1 flex items-center gap-1 text-sm">
                  <div className="bg-status-error h-1 w-1 rounded-full"></div>
                  {err.title}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="mb-1 flex items-center gap-3">
                <div
                  className="bg-accent h-6 w-1 rounded"
                  aria-hidden="true"
                ></div>
                <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                  <FileText className="text-primary h-5 w-5" />
                  Contenido
                </h3>
              </div>
              <RichTextEditor
                value={data.content}
                onChange={(html) => onChange({ content: html })}
                placeholder="Escribe el contenido de tu post..."
                className="text-base"
                minHeight="220px"
              />
              {err.content && (
                <div className="text-destructive mt-1 flex items-center gap-1 text-sm">
                  <div className="bg-status-error h-1 w-1 rounded-full"></div>
                  {err.content}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-secondary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <Tag className="text-secondary h-5 w-5" />
                Clasificación
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Categoría
                </Label>
                <Select
                  value={data.category_id}
                  onValueChange={(value) => onChange({ category_id: value })}
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                    <SelectValue placeholder="Selecciona una categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Visibilidad
                </Label>
                <Select
                  value={data.visibility}
                  onValueChange={(value) =>
                    onChange({ visibility: value as Visibility })
                  }
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                    <SelectValue placeholder="Alcance" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="neighborhood">Barrio</SelectItem>
                    <SelectItem value="city">Ciudad</SelectItem>
                    <SelectItem value="country">País</SelectItem>
                    <SelectItem value="global">Global</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Barrio
                </Label>
                <Select
                  value={data.neighborhood_id}
                  onValueChange={(value) =>
                    onChange({ neighborhood_id: value })
                  }
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                    <SelectValue placeholder="Selecciona un barrio (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {neighborhoods.map((n) => (
                      <SelectItem key={n.id} value={String(n.id)}>
                        {n.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {can_pin && (
              <div className="flex items-center gap-3">
                <input
                  id="is_pinned"
                  type="checkbox"
                  checked={Boolean(data.is_pinned)}
                  onChange={(e) =>
                    onChange({ is_pinned: Boolean(e.target.checked) })
                  }
                  className="accent-primary h-4 w-4"
                />
                <Label htmlFor="is_pinned" className="text-sm">
                  Fijar (mostrar como destacado)
                </Label>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-accent h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <Tag className="text-primary h-5 w-5" />
                Tags
              </h3>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="md:col-span-2">
                <TagsInput
                  tags={(data.tags ?? "")
                    .split(",")
                    .map((t) => t.trim())
                    .filter(Boolean)}
                  onChange={(arr) => onChange({ tags: arr.join(", ") })}
                  placeholder="Escribe y presiona Enter o coma"
                  maxTags={10}
                  maxLengthPerTag={20}
                />
                {err.tags && (
                  <div className="text-destructive mt-1 flex items-center gap-1 text-sm">
                    <div className="bg-status-error h-1 w-1 rounded-full"></div>
                    {err.tags}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="border-border flex justify-end gap-4 border-t pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="h-9 bg-transparent px-4 text-sm font-medium"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="h-9 px-4 text-sm font-medium"
            >
              {submitLabel}
            </Button>
          </div>
        </form>
      </CardContent>
    </div>
  )
}
